/**
 * SyncContext - Manages save/sync state for the entire app
 * Tracks pending operations and provides feedback on save status
 * Integrates with syncManager for offline queue
 */

import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { syncManager, QueueableAction, DisabledOffline } from '../services/sync'

/**
 * Sync states
 */
export const SyncState = {
  IDLE: 'idle',       // No activity, nothing to show
  SAVED: 'saved',     // Just saved successfully (green, fades)
  SAVING: 'saving',   // Save in progress (gray spinner)
  OFFLINE: 'offline', // Offline with queued items (yellow)
  FAILED: 'failed',   // Save failed (red with retry)
}

const SyncContext = createContext(null)

export function SyncProvider({ children }) {
  const [syncState, setSyncState] = useState(SyncState.IDLE)
  const [queuedCount, setQueuedCount] = useState(syncManager.getQueueCount())
  const [isOnline, setIsOnline] = useState(syncManager.isOnline)
  const [conflictCount, setConflictCount] = useState(0)
  const [failedOperation, setFailedOperation] = useState(null)
  const fadeTimer = useRef(null)

  // Subscribe to sync manager state changes
  useEffect(() => {
    const unsubscribe = syncManager.subscribe((state) => {
      setQueuedCount(state.queueCount)
      setConflictCount(state.conflictCount)
      setIsOnline(state.isOnline)

      // Update sync state based on queue
      if (!state.isOnline && state.queueCount > 0) {
        setSyncState(SyncState.OFFLINE)
      }
    })

    return unsubscribe
  }, [])

  /**
   * Start a save operation
   */
  const startSaving = useCallback(() => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current)
      fadeTimer.current = null
    }
    setSyncState(SyncState.SAVING)
  }, [])

  /**
   * Mark save as successful
   * Shows "Saved ✓" then fades after 2 seconds
   */
  const saveDone = useCallback(() => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current)
    }
    setSyncState(SyncState.SAVED)

    // Fade to idle after 2 seconds
    fadeTimer.current = setTimeout(() => {
      setSyncState(SyncState.IDLE)
      fadeTimer.current = null
    }, 2000)
  }, [])

  /**
   * Mark save as failed
   * @param {Object} operation - The failed operation for retry
   */
  const saveFailed = useCallback((operation = null) => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current)
      fadeTimer.current = null
    }
    setSyncState(SyncState.FAILED)
    setFailedOperation(operation)
  }, [])

  /**
   * Set offline mode with queued item count
   */
  const setOfflineState = useCallback(() => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current)
      fadeTimer.current = null
    }
    setSyncState(SyncState.OFFLINE)
  }, [])

  /**
   * Reset to idle state
   */
  const reset = useCallback(() => {
    if (fadeTimer.current) {
      clearTimeout(fadeTimer.current)
      fadeTimer.current = null
    }
    setSyncState(SyncState.IDLE)
    setFailedOperation(null)
  }, [])

  /**
   * Retry the failed operation
   * @returns {Object|null} The failed operation to retry
   */
  const retry = useCallback(() => {
    const op = failedOperation
    setFailedOperation(null)
    setSyncState(SyncState.SAVING)
    return op
  }, [failedOperation])

  /**
   * Queue an action for offline sync
   * @param {Object} action - Action to queue
   */
  const queueAction = useCallback((action) => {
    const id = syncManager.queueAction(action)
    setOfflineState()
    return id
  }, [setOfflineState])

  /**
   * Check if an action can be performed offline
   * @param {string} actionType - Action type
   * @returns {boolean}
   */
  const canPerformOffline = useCallback((actionType) => {
    if (isOnline) return true
    return syncManager.canQueueAction(actionType)
  }, [isOnline])

  /**
   * Check if action is disabled (requires online)
   * @param {string} actionType - Action type
   * @returns {boolean}
   */
  const isActionDisabled = useCallback((actionType) => {
    if (isOnline) return false
    return syncManager.isDisabledOffline(actionType)
  }, [isOnline])

  /**
   * Wrapper for async operations that handles sync state
   * Queues action if offline and queueable
   * @param {Function} asyncFn - The async function to execute
   * @param {Object} options - Options for queuing
   * @returns {Promise} The result of the async function
   */
  const withSync = useCallback(async (asyncFn, options = {}) => {
    const { queueable = null, action = null } = options

    // If offline and action is queueable, queue it
    if (!isOnline && queueable && action) {
      if (syncManager.canQueueAction(action.action)) {
        queueAction(action)
        // Return a mock success for optimistic UI
        return { queued: true, id: action.id }
      } else {
        throw new Error('This action cannot be performed offline')
      }
    }

    startSaving()
    try {
      const result = await asyncFn()
      saveDone()
      return result
    } catch (error) {
      // If network error and action is queueable, queue it
      if (!navigator.onLine && queueable && action && syncManager.canQueueAction(action.action)) {
        queueAction(action)
        return { queued: true, id: action.id }
      }
      saveFailed({ asyncFn, error, action })
      throw error
    }
  }, [isOnline, startSaving, saveDone, saveFailed, queueAction])

  const value = {
    // State
    syncState,
    queuedCount,
    conflictCount,
    failedOperation,
    isOnline,

    // Computed
    isSaving: syncState === SyncState.SAVING,
    isSaved: syncState === SyncState.SAVED,
    isOffline: syncState === SyncState.OFFLINE || !isOnline,
    isFailed: syncState === SyncState.FAILED,

    // Actions
    startSaving,
    saveDone,
    saveFailed,
    setOfflineState,
    reset,
    retry,
    queueAction,
    withSync,

    // Offline helpers
    canPerformOffline,
    isActionDisabled,

    // Direct access to sync manager
    syncManager,
  }

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  )
}

/**
 * Hook to access sync context
 */
export function useSync() {
  const context = useContext(SyncContext)
  if (!context) {
    throw new Error('useSync must be used within a SyncProvider')
  }
  return context
}

// Re-export for convenience
export { QueueableAction, DisabledOffline }

export default SyncContext
